import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { pingFrame, updateHandoff } from "../actions/sharedActions";
import { PingResponse } from "../types/api";
import posthog from "posthog-js";
import { ensureAnalyticsInitialized } from "../analytics";
import { ApiError } from "./types";
import { setFrameStorage } from "../storage";
import { getEngagementActions } from "../actions/engage";

export interface UserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  external_id?: string;
  external_attributes?: Record<string, any>;
  email_signature?: string;
  phone_signature?: string;
  external_id_signature?: string;
}

export interface SingleUserState {
  user_data: UserData;
}

export interface UserState {
  data: SingleUserState; // keyed by frame_id
  status: "idle" | "loading" | "succeeded" | "failed";
  error: ApiError | null; // NOTE(memben): this error can be any API error, not just user errors
}

const initialState: UserState = {
  data: {
    user_data: {},
  },
  status: "idle",
  error: null,
};

interface UpdatePingData {
  frameId: string;
  user_data: PingResponse["data"]["user_data"];
  headers: PingResponse["headers"];
}

function updateFromPing(state: any, action: { payload: UpdatePingData }) {
  const frameId = action.payload.frameId;
  const {
    email,
    phone,
    external_attributes,
    external_id,
    last_name,
    first_name,
  } = action.payload.user_data;
  state.data = {
    user_data: {
      // keep signatures in store
      ...state.data.user_data,
      email: email || state.data.user_data.email,
      phone: phone || state.data.user_data.phone,
      external_attributes:
        external_attributes || state.data.user_data.external_attributes,
      external_id: external_id || state.data.user_data.external_id,
      last_name: last_name || state.data.user_data.last_name,
      first_name: first_name || state.data.user_data.first_name,
    },
  };
  const sessionId = action.payload.headers.session_id;
  const userId = action.payload.headers.user_id;
  const deviceId = action.payload.headers.device_id;
  setFrameStorage(frameId, { sessionId, userId, deviceId });
  if (sessionId) {
    ensureAnalyticsInitialized();
    if (posthog.get_distinct_id() !== userId) posthog.identify(userId);
  }
}

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUserFromPing: updateFromPing,
    // WARNING(liamvdv): updateUserData will trigger a backgorund ping request,
    //                   do not call for frequent/small updates.
    //                   see middlewares.ts. Use more granular
    updateUserDataAndPing: (
      state,
      action: PayloadAction<Partial<UserData>>
    ) => {
      Object.assign(state.data.user_data, action.payload);
    },
    setUserError: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.status = "failed";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(pingFrame.pending, (state, action) => {
        if (action.meta.arg.background) return;
        state.status = "loading";
        state.error = null;
      })
      .addCase(pingFrame.fulfilled, (state, action) => {
        state.status = "succeeded";
        const data = {
          frameId: action.meta.arg.frame_id,
          user_data: action.payload.data.user_data,
          headers: action.payload.headers,
        };
        updateFromPing(state, { payload: data });
        state.error = null;
      })
      .addCase(pingFrame.rejected, (state, action) => {
        if (action.meta.arg.background && !action.payload?.body.sdk_action)
          return;
        state.status = "failed";
        state.error = action.payload || {
          status: 500,
          headers: {},
          body: { errors: [{ message: "Unknown error" }] },
        };
      })
      .addCase(updateHandoff.fulfilled, (state, action) => {
        const {
          frameId,
          headers: { session_id, user_id, device_id },
        } = action.payload;
        setFrameStorage(frameId, {
          sessionId: session_id,
          userId: user_id,
          deviceId: device_id,
        });
      })
      .addCase(getEngagementActions.fulfilled, (state, action) => {
        updateFromPing(state, {
          payload: {
            user_data: action.payload.data.user_data,
            headers: action.payload.headers,
            frameId: action.payload.frameId,
          },
        });
      });
  },
});

export const { updateUserFromPing, updateUserDataAndPing, setUserError } =
  userSlice.actions;
export const userReducer = userSlice.reducer;
